import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { systemStatusService, SystemStatus, SystemAlert } from '../services/systemStatusService';

interface SystemAlertContextType {
  alerts: SystemAlert[];
  services: SystemStatus['services'];
  isLoading: boolean;
  lastChecked: Date | null;
  dismissAlert: (alertId: string) => void;
  refreshStatus: () => Promise<void>;
}

const SystemAlertContext = createContext<SystemAlertContextType | undefined>(undefined);

interface SystemAlertProviderProps {
  children: ReactNode;
  pollInterval?: number; // in milliseconds
}

const DISMISSED_ALERTS_KEY = 'rnkrocket:dismissed-alerts';

/**
 * SystemAlertProvider - Provides system health status and alerts to the app
 *
 * Features:
 * - Polls backend health endpoint every 60 seconds
 * - Tracks dismissed alerts in session storage
 * - Provides service status (database, AI, email)
 */
export const SystemAlertProvider: React.FC<SystemAlertProviderProps> = ({
  children,
  pollInterval = 60000, // 60 seconds default
}) => {
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [services, setServices] = useState<SystemStatus['services']>({
    database: 'healthy',
    ai: 'healthy',
    email: 'healthy',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [dismissedAlertIds, setDismissedAlertIds] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(DISMISSED_ALERTS_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const fetchStatus = useCallback(async () => {
    try {
      const status = await systemStatusService.getSystemStatus();

      // Filter out dismissed alerts
      const activeAlerts = status.alerts.filter(
        (alert) => !dismissedAlertIds.has(alert.id)
      );

      setAlerts(activeAlerts);
      setServices(status.services);
      setLastChecked(new Date());
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      // Don't update state on error - keep previous status
    } finally {
      setIsLoading(false);
    }
  }, [dismissedAlertIds]);

  const dismissAlert = useCallback((alertId: string) => {
    setDismissedAlertIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(alertId);

      // Persist to session storage
      try {
        sessionStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify([...newSet]));
      } catch (error) {
        console.error('Failed to persist dismissed alerts:', error);
      }

      return newSet;
    });

    // Remove from active alerts
    setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  }, []);

  const refreshStatus = useCallback(async () => {
    setIsLoading(true);
    await fetchStatus();
  }, [fetchStatus]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll for updates
  useEffect(() => {
    const intervalId = setInterval(fetchStatus, pollInterval);
    return () => clearInterval(intervalId);
  }, [fetchStatus, pollInterval]);

  const value: SystemAlertContextType = {
    alerts,
    services,
    isLoading,
    lastChecked,
    dismissAlert,
    refreshStatus,
  };

  return (
    <SystemAlertContext.Provider value={value}>
      {children}
    </SystemAlertContext.Provider>
  );
};

export const useSystemAlerts = (): SystemAlertContextType => {
  const context = useContext(SystemAlertContext);
  if (context === undefined) {
    throw new Error('useSystemAlerts must be used within a SystemAlertProvider');
  }
  return context;
};

export default SystemAlertContext;
